import api from './api';

class PedidoCompraService {
  /**
   * Criar novo pedido de compra
   */
  async criar(pedidoData) {
    const response = await api.post(`/pedidos-compra`, pedidoData);
    // Backend retorna { message: '...', pedido: {...} }
    return response.data.pedido || response.data;
  }

  /**
   * Listar pedidos com filtros
   */
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    if (filtros.fornecedorId) params.append('fornecedorId', filtros.fornecedorId);
    if (filtros.obraId) params.append('obraId', filtros.obraId);
    if (filtros.statusId) params.append('statusId', filtros.statusId); // Atualizado para statusId
    if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
    if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
    if (filtros.valorMin) params.append('valorMinimo', filtros.valorMin);
    if (filtros.valorMax) params.append('valorMaximo', filtros.valorMax);
    if (filtros.numero) params.append('numeroOC', filtros.numero);
    if (filtros.page) params.append('page', filtros.page);
    if (filtros.limit) params.append('limit', filtros.limit);

    const response = await api.get(`/pedidos-compra?${params.toString()}`);
    
    // Mapear os dados para o formato esperado pelo frontend
    const pedidos = response.data.pedidos || response.data.records || [];
    
    return {
      pedidos: pedidos.map(pedido => ({
        ...pedido,
        numero: pedido.numeroOC,
        valorTotal: pedido.valorFinal,
        fornecedor: pedido.fornecedor ? {
          ...pedido.fornecedor,
          nome: pedido.fornecedor.nomeFornecedor
        } : null,
        obra: pedido.obra ? {
          ...pedido.obra,
          nome: pedido.obra.nomeProjeto
        } : null,
        // Manter o objeto status para uso posterior, mas garantir que não cause erro no render
        status: pedido.status || null,
        statusNome: pedido.status?.nome || 'Sem Status',
        statusId: pedido.statusId
      })),
      total: response.data.total,
      page: response.data.page,
      totalPages: response.data.totalPages
    };
  }

  /**
   * Buscar pedido por ID
   */
  async buscarPorId(id) {
    const response = await api.get(`/pedidos-compra/${id}`);
    const pedido = response.data;
    
    // Mapear os dados para o formato esperado
    return {
      ...pedido,
      numero: pedido.numeroOC,
      valorTotal: pedido.valorFinal,
      fornecedor: pedido.fornecedor ? {
        ...pedido.fornecedor,
        nome: pedido.fornecedor.nomeFornecedor
      } : null,
      obra: pedido.obra ? {
        ...pedido.obra,
        nome: pedido.obra.nomeProjeto
      } : null,
      statusNome: pedido.status?.nome || 'Sem Status',
      statusId: pedido.statusId,
      itens: pedido.itens || []
    };
  }

  /**
   * Atualizar pedido
   */
  async atualizar(id, pedidoData) {
    const response = await api.put(`/pedidos-compra/${id}`, pedidoData);
    // Backend retorna { message: '...', pedido: {...} }
    return response.data.pedido || response.data;
  }

  /**
   * Excluir pedido
   */
  async excluir(id) {
    const response = await api.delete(`/pedidos-compra/${id}`);
    return response.data;
  }

  /**
   * Enviar pedido para orçamento
   */
  async enviarParaOrcamento(id) {
    const response = await api.post(`/pedidos-compra/${id}/enviar-orcamento`);
    return response.data;
  }

  /**
   * Enviar pedido para aprovação
   */
  async enviarParaAprovacao(id) {
    const response = await api.post(`/pedidos-compra/${id}/enviar-aprovacao`);
    return response.data;
  }

  /**
   * Aprovar pedido
   */
  async aprovar(id, comentario = '', statusAprovadoId = null) {
    const response = await api.patch(`/pedidos-compra/${id}/aprovar`, { 
      comentario,
      statusAprovadoId 
    });
    return response.data;
  }

  /**
   * Rejeitar pedido
   */
  async rejeitar(id, comentario = '', statusRejeitadoId = null) {
    const response = await api.patch(`/pedidos-compra/${id}/rejeitar`, {
      comentario,
      statusRejeitadoId
    });
    return response.data;
  }

  /**
   * Mudar status do pedido (método genérico)
   */
  async mudarStatus(id, statusId, comentario = '') {
    const response = await api.patch(`/pedidos-compra/${id}/status`, {
      statusId,
      comentario
    });
    return response.data;
  }

  /**
   * Listar todos os status disponíveis para Pedido de Compra
   */
  async listarStatus() {
    const response = await api.get('/pedidos-compra/status');
    return response.data.status || [];
  }

  /**
   * Marcar como em andamento
   */
  async marcarEmAndamento(id, comentario = '') {
    const response = await api.patch(`/pedidos-compra/${id}/em-andamento`, { comentario });
    return response.data;
  }

  /**
   * Marcar como recebido
   */
  async marcarRecebido(id, comentario = '') {
    const response = await api.patch(`/pedidos-compra/${id}/recebido`, { comentario });
    return response.data;
  }

  /**
   * Finalizar pedido
   */
  async finalizar(id, comentario = '') {
    const response = await api.patch(`/pedidos-compra/${id}/finalizar`, { comentario });
    return response.data;
  }

  /**
   * Buscar histórico do pedido
   */
  async buscarHistorico(id) {
    const response = await api.get(`/pedidos-compra/${id}/historico`);
    return response.data;
  }

  /**
   * Gerar próximo número
   * Nota: O número é gerado automaticamente pelo backend ao criar o pedido
   */
  async gerarNumero() {
    // Retorna vazio - o número real será gerado pelo backend ao salvar
    return { numero: "" };
  }

  /**
   * Validar se o número da OC é único
   */
  async validarNumeroUnico(numero) {
    try {
      const response = await api.get(`/pedidos-compra/validar-numero/${encodeURIComponent(numero)}`);
      return response.data; // { existe: boolean, mensagem: string }
    } catch (error) {
      console.error('Erro ao validar número:', error);
      throw error;
    }
  }

  /**
   * Vincular ou desvincular nota fiscal
   */
  async vincularNotaFiscal(id, notaFiscalId = null) {
    try {
      const response = await api.patch(`/pedidos-compra/${id}/vincular-nota-fiscal`, {
        notaFiscalId
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao vincular nota fiscal:', error);
      throw error;
    }
  }

  /**
   * Buscar estatísticas
   */
  async estatisticas() {
    const response = await api.get(`/pedidos-compra/dashboard`);
    return response.data;
  }

  /**
   * Exportar relatório
   */
  async exportar(filtros = {}, formato = 'excel') {
    const params = new URLSearchParams({ formato, ...filtros });
    
    const response = await api.get(`/pedidos-compra/exportar?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Criar download do arquivo
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pedidos-compra.${formato === 'pdf' ? 'pdf' : 'xlsx'}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return response.data;
  }
}

const pedidoCompraService = new PedidoCompraService();
export default pedidoCompraService;
